import { ACTIVITY_TAXONOMY_SELECTION_LIST_KEYS } from '../../../../shared/selection-lists/types';
import { customSkillDefinition } from '../definitions/customSkill';
import { geographyDefinition } from '../definitions/geography';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type TreeFilter,
} from '../types/filters';

const TREEMAP_DRILLDOWN_SEGMENTS = [
  'geography',
  'role',
  'rics',
  'skills',
  'activities',
] as const;

export type TreemapDrilldownSegment =
  (typeof TREEMAP_DRILLDOWN_SEGMENTS)[number];

export const ROLE_DRILLDOWN_LEVEL_ORDER = [
  'role_k7',
  'role_k150',
  'role_k1500',
  'role_k15000',
] as const;

const ROLE_FINE_DRILLDOWN_LEVEL_ORDER = [
  'role_k7',
  'role_k150',
  'role_k1500',
  'role_k5000',
  'role_k15000',
] as const;

export type RoleLadder = 'standard' | 'fine';

const LEVEL_ORDER_BY_SEGMENT: Record<
  TreemapDrilldownSegment,
  readonly string[]
> = {
  geography: geographyDefinition.selectionListIds,
  role: ROLE_DRILLDOWN_LEVEL_ORDER,
  rics: ['rics_k10', 'rics_k50', 'rics_k400', 'company'],
  skills: customSkillDefinition.selectionListIds,
  activities: ACTIVITY_TAXONOMY_SELECTION_LIST_KEYS,
};

export function getTreemapDrilldownLevelOrder(
  segment: TreemapDrilldownSegment,
  /**
   * The active geography taxonomy's level keys, from `useGeographyTaxonomy`.
   * Only defaulted for non-geography segments, which ignore it; a geography
   * caller that omits it silently gets the metro level set.
   */
  geographySelectionListIds: readonly string[] = geographyDefinition.selectionListIds,
  roleLadder: RoleLadder = 'standard'
): readonly string[] {
  if (segment === 'geography') {
    return geographySelectionListIds;
  }
  if (segment === 'role' && roleLadder === 'fine') {
    return ROLE_FINE_DRILLDOWN_LEVEL_ORDER;
  }
  return LEVEL_ORDER_BY_SEGMENT[segment];
}

export function getTreemapDrilldownIsLeafLevel(
  segment: TreemapDrilldownSegment,
  levelId: string,
  /**
   * Required for geography: the deepest level differs per taxonomy (`city` on
   * metro, `county` on administrative). Omitting it compares against the metro
   * default, which marks a terminal county cell drillable and lets a click
   * drill past the taxonomy's last level.
   */
  geographySelectionListIds?: readonly string[],
  roleLadder: RoleLadder = 'standard'
): boolean {
  const levelOrder = getTreemapDrilldownLevelOrder(
    segment,
    geographySelectionListIds,
    roleLadder
  );
  const lastLevel = levelOrder[levelOrder.length - 1];
  return lastLevel !== undefined && levelId === lastLevel;
}

export function isTreemapDrilldownSegment(
  dim: string | undefined
): dim is TreemapDrilldownSegment {
  return TREEMAP_DRILLDOWN_SEGMENTS.includes(dim as TreemapDrilldownSegment);
}

export function isFlatTreemapSegment(segment: string | undefined): boolean {
  return segment === 'rics';
}

export function isOverlappingTreemapSegment(
  segment: TreemapDrilldownSegment
): boolean {
  return segment === 'skills' || segment === 'activities';
}

// The rics segment spans two filters: its level order runs from industries
// (Industry filter) down to companies (Company filter).
const FILTER_IDS_BY_SEGMENT: Record<TreemapDrilldownSegment, FilterId[]> = {
  geography: [FilterId.GEOGRAPHY, FilterId.MULTI_GEOGRAPHY],
  role: [FilterId.ROLE, FilterId.MULTI_ROLE],
  rics: [
    FilterId.COMPANY,
    FilterId.MULTI_COMPANY,
    FilterId.INDUSTRY,
    FilterId.MULTI_INDUSTRY,
  ],
  skills: [FilterId.CUSTOM_SKILL, FilterId.MULTI_CUSTOM_SKILL],
  activities: [FilterId.ACTIVITY, FilterId.MULTI_ACTIVITY],
};

function getTreeSelectionsFromFilter(
  filter: Filter
): TreeFilter['selections'] | null {
  if (filter.type === FilterType.TREE) {
    return (filter as TreeFilter).selections;
  }
  if (isMultiFilter(filter) && filter.instances.length > 0) {
    const firstInstance = filter.instances[0];
    if (firstInstance !== undefined && firstInstance.type === FilterType.TREE) {
      return (firstInstance as TreeFilter).selections;
    }
  }
  return null;
}

export function getTreemapInitialLevelOffset(
  segment: TreemapDrilldownSegment,
  effectiveFilters: Filter[],
  /**
   * Required for geography: a `state`/`county` selection is absent from the
   * metro default, so omitting it silently reports offset 0 and the chart
   * starts at the top level instead of below the active selection.
   */
  geographySelectionListIds: readonly string[]
): number {
  const targetIds = FILTER_IDS_BY_SEGMENT[segment];
  const selections = effectiveFilters
    .filter((f) => targetIds.includes(f.id))
    .flatMap((f) => getTreeSelectionsFromFilter(f) ?? []);
  if (selections.length === 0) return 0;

  const levelOrder = getTreemapDrilldownLevelOrder(
    segment,
    geographySelectionListIds
  );
  const deepestIndex = selections.reduce((max, sel) => {
    const idx = levelOrder.indexOf(sel.levelId);
    return idx === -1 ? max : Math.max(max, idx);
  }, -1);
  if (deepestIndex === -1) return 0;
  return deepestIndex + 1;
}
